import { ComponentProps } from 'react';
import { Tag } from '@carbon/react';
import type { BulkType, SubclusterStatus } from './types';

// Tag's props are a union; distribute to pull `type` off the members that
// carry it.
type TypeProp<P> = P extends { type?: infer T } ? T : never;
export type TagColor = TypeProp<ComponentProps<typeof Tag>>;

// The single source for a sub-cluster status's labels and colours, keyed by
// status. Three files used to keep their own maps and drifted on the three
// casings the same status wore and on colour. One table now holds:
//   - `label`   the shared entity/batch tag label (Title Case; combined reads
//               "Merged" on an entity run, "Combined" on an address run);
//   - `filter`  the filter option label (Title Case, both run types);
//   - `color`   the tag colour per run type (deliberately different);
//   - `amber`   the address tint for the partial states.
// The address run-row tag text (lowercase, mock v7) is the address label
// lowercased — derived via `subclusterAddressTag`, not stored.
export interface StatusMeta {
  label: Record<BulkType, string>;
  filter: string;
  color: Record<BulkType, TagColor>;
  amber?: boolean;
}

export const STATUS_META: Record<SubclusterStatus, StatusMeta> = {
  in_scope: {
    label: { entities: 'Included', addresses: 'Included' },
    filter: 'Included',
    color: { entities: 'teal', addresses: 'high-contrast' },
  },
  excluded: {
    label: { entities: 'Excluded', addresses: 'Excluded' },
    filter: 'Excluded',
    color: { entities: 'gray', addresses: 'outline' },
  },
  partially_excluded: {
    label: { entities: 'Partially excluded', addresses: 'Partially excluded' },
    filter: 'Partially Excluded',
    color: { entities: 'cyan', addresses: 'warm-gray' },
    amber: true,
  },
  combined: {
    label: { entities: 'Merged', addresses: 'Combined' },
    filter: 'Combined',
    color: { entities: 'green', addresses: 'green' },
  },
  failed: {
    label: { entities: 'Failed', addresses: 'Failed' },
    filter: 'Failed',
    color: { entities: 'red', addresses: 'red' },
  },
  reversed: {
    label: { entities: 'Reversed', addresses: 'Reversed' },
    filter: 'Reversed',
    color: { entities: 'purple', addresses: 'cool-gray' },
  },
  partially_reversed: {
    label: { entities: 'Partially reversed', addresses: 'Partially reversed' },
    filter: 'Partially Reversed',
    color: { entities: 'warm-gray', addresses: 'warm-gray' },
    amber: true,
  },
};

export const SUBCLUSTER_STATUSES = Object.keys(
  STATUS_META
) as SubclusterStatus[];

// The shared entity/batch tag label.
export function subclusterStatusLabel(
  status: SubclusterStatus,
  bulkType: BulkType
): string {
  return STATUS_META[status].label[bulkType];
}

// The address run-row tag text: the address label, lowercased (mock v7).
export function subclusterAddressTag(status: SubclusterStatus): string {
  return STATUS_META[status].label.addresses.toLowerCase();
}
